/**
 * PPTX Parser with Color Extraction
 * Reads PPTX XML directly to preserve run-level colors
 */
import JSZip from 'jszip';

export async function parsePptxWithColors(file) {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  const segments = [];
  let segId = 1;

  // Find all slide files
  const slideFiles = Object.keys(zip.files)
    .filter(f => f.match(/ppt\/slides\/slide\d+\.xml/))
    .sort((a, b) => {
      const na = parseInt(a.match(/slide(\d+)/)[1]);
      const nb = parseInt(b.match(/slide(\d+)/)[1]);
      return na - nb;
    });

  for (let slideIdx = 0; slideIdx < slideFiles.length; slideIdx++) {
    const slideXml = await zip.file(slideFiles[slideIdx])?.async('string');
    if (!slideXml) continue;

    const parser = new DOMParser();
    const doc = parser.parseFromString(slideXml, 'text/xml');

    // Get all text paragraphs
    const paras = doc.getElementsByTagName('a:p');
    for (const para of paras) {
      const runs = para.getElementsByTagName('a:r');
      if (!runs.length) continue;

      let fullText = '';
      const htmlParts = [];

      for (const run of runs) {
        const tEl = run.querySelector('a\\:t, t');
        if (!tEl || !tEl.textContent.trim()) continue;

        const text = tEl.textContent;
        fullText += text;

        // Extract run properties
        const rPr = run.querySelector('a\\:rPr, rPr');
        const spanStyles = [];

        if (rPr) {
          if (rPr.getAttribute('b') === '1') spanStyles.push('font-weight:bold');
          if (rPr.getAttribute('i') === '1') spanStyles.push('font-style:italic');
          if (rPr.getAttribute('u') === 'sng') spanStyles.push('text-decoration:underline');
          const sz = rPr.getAttribute('sz');
          if (sz) spanStyles.push(`font-size:${parseInt(sz)/100}pt`);

          // Color
          const solidFill = rPr.querySelector('a\\:solidFill, solidFill');
          if (solidFill) {
            const srgbClr = solidFill.querySelector('a\\:srgbClr, srgbClr');
            if (srgbClr) {
              const val = srgbClr.getAttribute('val');
              if (val && val !== '000000') spanStyles.push(`color:#${val}`);
            }
          }
        }

        if (spanStyles.length > 0) {
          htmlParts.push(`<span style="${spanStyles.join(';')}">${text}</span>`);
        } else {
          htmlParts.push(text);
        }
      }

      fullText = fullText.trim();
      if (!fullText) continue;

      const sourceHtml = htmlParts.join('');
      const hasColor = sourceHtml !== fullText;

      // Paragraph alignment
      const pPr = para.querySelector('a\\:pPr, pPr');
      const algn = pPr?.getAttribute('algn');
      const alignMap = { ctr: 'center', r: 'right', l: 'left' };

      segments.push({
        id: segId++,
        source: fullText,
        sourceHtml: hasColor ? sourceHtml : fullText,
        target: '',
        targetHtml: '',
        status: 'empty',
        hasColorFormatting: hasColor,
        formatting: {
          is_pptx: true,
          slide: slideIdx + 1,
          alignment: algn ? alignMap[algn] : 'left'
        }
      });
    }
  }

  return segments;
}

export default { parsePptxWithColors };
